import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "../lib/api";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { Compass, MailCheck, ArrowLeft } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1502003148287-a82ef80a6abc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxhdXRoZW50aWMlMjB0cmF2ZWwlMjBsaWZlc3R5bGV8ZW58MHx8fHwxNzg2MzI1MTU0fDA&ixlib=rb-4.1.0&q=85";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Seo title="Forgot password" description="Request a secure link to reset your WanderlyMe password." path="/forgot-password" noindex />
    <div className="flex-1 grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="travel" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A3B2B]/70 to-transparent" />
        <Link to="/" className="absolute top-8 left-8 flex items-center gap-2 text-[#FDFBF7]">
          <Compass className="w-6 h-6" />
          <span className="font-display text-2xl">WanderlyMe</span>
        </Link>
      </div>

      <div className="flex items-center justify-center p-8 grain relative">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          {sent ? (
            <div data-testid="forgot-sent">
              <MailCheck className="w-10 h-10 text-[#1A3B2B] mb-5" />
              <h1 className="font-display text-4xl tracking-tight mb-2">Check your inbox</h1>
              <p className="text-muted-foreground mb-8">
                If an account exists for <span className="text-foreground font-medium">{email}</span>, we've sent a
                link to reset your password. The link expires in 1 hour.
              </p>
              <Link
                to="/auth"
                className="inline-flex items-center gap-1 text-sm text-[#1A3B2B] hover:text-[#C8553D] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-4xl tracking-tight mb-1">Forgot password</h1>
              <p className="text-muted-foreground mb-8">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={submit} className="space-y-4">
                <input
                  data-testid="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
                />
                {error && <p className="text-sm text-[#C8553D]">{error}</p>}
                <button
                  type="submit"
                  data-testid="forgot-submit"
                  disabled={loading}
                  className="w-full py-3 rounded-full bg-[#1A3B2B] text-[#FDFBF7] hover:bg-[#122a1f] transition-colors disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Send reset link"}
                </button>
              </form>
              <Link
                to="/auth"
                className="mt-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-[#C8553D] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </Link>
            </>
          )}
        </motion.div>
      </div>
    </div>
      <Footer />
    </div>
  );
}
